/**
 * Calendly Integration Hooks
 *
 * React Query hooks for Calendly integration
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/lib/auth'
import {
  getCalendlyIntegration,
  getCalendlySchedules,
  getDefaultSchedule,
  getEpisodeSchedule,
  initiateCalendlyOAuth,
  syncCalendlyEventTypes,
  setDefaultSchedule,
  linkScheduleToEpisode,
  disconnectCalendly,
  createBookingLink,
} from './api'

/**
 * Hook to get Calendly integration status
 */
export function useCalendlyIntegration() {
  const { membership } = useAuth()
  const teamId = membership?.team_id

  return useQuery({
    queryKey: ['integration', 'calendly', teamId],
    queryFn: () => getCalendlyIntegration(teamId!),
    enabled: !!teamId,
  })
}

/**
 * Hook to get all Calendly schedules for the team
 */
export function useCalendlySchedules() {
  const { membership } = useAuth()
  const teamId = membership?.team_id

  return useQuery({
    queryKey: ['calendly-schedules', teamId],
    queryFn: () => getCalendlySchedules(teamId!),
    enabled: !!teamId,
  })
}

/**
 * Hook to get the default schedule
 */
export function useDefaultSchedule() {
  const { membership } = useAuth()
  const teamId = membership?.team_id

  return useQuery({
    queryKey: ['calendly-schedules', 'default', teamId],
    queryFn: () => getDefaultSchedule(teamId!),
    enabled: !!teamId,
  })
}

/**
 * Hook to get schedule for a specific episode
 */
export function useEpisodeSchedule(episodeId: string | undefined) {
  return useQuery({
    queryKey: ['episode-schedule', episodeId],
    queryFn: () => getEpisodeSchedule(episodeId!),
    enabled: !!episodeId,
  })
}

/**
 * Hook to initiate Calendly OAuth
 */
export function useInitiateCalendlyOAuth() {
  const { membership } = useAuth()
  const teamId = membership?.team_id

  return useMutation({
    mutationFn: (redirectPath?: string) =>
      initiateCalendlyOAuth(teamId!, redirectPath),
  })
}

/**
 * Hook to sync Calendly event types
 */
export function useSyncCalendlyEventTypes() {
  const { membership } = useAuth()
  const teamId = membership?.team_id
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => syncCalendlyEventTypes(teamId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendly-schedules', teamId] })
      queryClient.invalidateQueries({ queryKey: ['integration', 'calendly', teamId] })
    },
  })
}

/**
 * Hook to set default schedule
 */
export function useSetDefaultSchedule() {
  const { membership } = useAuth()
  const teamId = membership?.team_id
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (scheduleId: string) => setDefaultSchedule(scheduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendly-schedules', teamId] })
    },
  })
}

/**
 * Hook to link schedule to episode
 */
export function useLinkScheduleToEpisode() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ episodeId, scheduleId }: { episodeId: string; scheduleId: string }) =>
      linkScheduleToEpisode(episodeId, scheduleId),
    onSuccess: (_, { episodeId }) => {
      queryClient.invalidateQueries({ queryKey: ['episode-schedule', episodeId] })
    },
  })
}

/**
 * Hook to disconnect Calendly
 */
export function useDisconnectCalendly() {
  const { membership } = useAuth()
  const teamId = membership?.team_id
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => disconnectCalendly(teamId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['integration', 'calendly', teamId] })
      queryClient.invalidateQueries({ queryKey: ['calendly-schedules', teamId] })
    },
  })
}

/**
 * Hook to create a booking link for a guest
 */
export function useCreateBookingLink() {
  return useMutation({
    mutationFn: ({ appearanceId, scheduleId }: { appearanceId: string; scheduleId: string }) =>
      createBookingLink(appearanceId, scheduleId),
  })
}

/**
 * Computed hook to check if Calendly is connected
 */
export function useIsCalendlyConnected() {
  const { data: integration, isLoading } = useCalendlyIntegration()

  return {
    isConnected: integration?.status === 'connected',
    isLoading,
    integration,
  }
}
